import { useDocumentTitle } from '@mantine/hooks'
import { IconChartBar, IconChevronRight } from '@tabler/icons-react'
import { type Report, reportLabels } from '@tnn/core'
import { EmptyState, type IconComponent, Page, useLinkComponent } from '@tnn/ui'
import { useTranslation } from 'react-i18next'
import type { RouteObject } from 'react-router'
import { useCan, useSession } from '../auth/session'
import { ReportPage } from './ReportPage'
import classes from './reports.module.css'

/**
 * One of an app's reports (`AppDefinition.reports`): one made from its definition (`defineReport`), its page at
 * `/reports/<name>`; or a page the app wrote itself, at `href` among its routes, named by i18n keys
 */
export type AppReport =
  | { report: Report; icon: IconComponent }
  | {
      key: string
      title: string
      description: string
      href: string
      icon: IconComponent
      /** Shown to those who may read it (or do `action` to it); none: to everyone in the app */
      resource?: string
      action?: string
    }

interface ReportLink {
  key: string
  title: string
  description: string
  href: string
  icon: IconComponent
}

/** The reports one may open, as their cards link them: a report of a resource one may not read is not there */
export function reportsFor(entries: readonly AppReport[], can: (action: string, subject: string) => boolean): ReportLink[] {
  return entries.flatMap((entry) => {
    if ('report' in entry) {
      const { report } = entry
      if (!can('read', report.resource.key)) return []
      const title = reportLabels.title(report)
      return [{ key: report.key, title, description: reportLabels.description(report), href: `/reports/${report.name}`, icon: entry.icon }]
    }
    return entry.resource && !can(entry.action ?? 'read', entry.resource) ? [] : [entry]
  })
}

/** The pages of an app's reports: the page of them all, and each defined one's own */
export function reportRoutes(entries: readonly AppReport[]): RouteObject[] {
  return [
    { path: 'reports', element: <ReportsPage entries={entries} /> },
    ...entries.flatMap((entry) =>
      'report' in entry ? [{ path: `reports/${entry.report.name}`, element: <ReportPage report={entry.report} /> }] : [],
    ),
  ]
}

/** An app's reports: one row each, what it tells and a link to it */
function ReportsPage({ entries }: { entries: readonly AppReport[] }) {
  const { t } = useTranslation()
  const { appId } = useSession()
  const can = useCan()
  const Link = useLinkComponent()
  const links = reportsFor(entries, can)
  useDocumentTitle(`${t('report.title')} · ${t(`apps.${appId}`)}`)
  return (
    <Page title={t('report.title')} subtitle={t('report.pick')}>
      {links.length === 0 ? (
        <EmptyState icon={IconChartBar} title={t('report.noneYours')} />
      ) : (
        <nav className={classes.list} aria-label={t('report.title')}>
          {links.map(({ key, title, description, href, icon: Icon }) => (
            <Link key={key} href={href} className={classes.row}>
              <span className={classes.icon}>
                <Icon size={22} stroke={1.75} />
              </span>
              <span className={classes.text}>
                <span className={classes.title}>{t(title)}</span>
                <span className={classes.description}>{t(description)}</span>
              </span>
              <IconChevronRight size={20} stroke={1.75} className={classes.chevron} />
            </Link>
          ))}
        </nav>
      )}
    </Page>
  )
}
